<template>
  <Modal
    ref="modal"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('rowEditModal.itemChip')"
    class="task-modal"
    :class="{ 'task-modal--with-activity': hasRightSidebar }"
    :close-button="false"
    :full-height="hasRightSidebar"
    :right-sidebar="hasRightSidebar"
    :content-scrollable="hasRightSidebar"
    :right-sidebar-scrollable="false"
    :collapsible-right-sidebar="false"
    @hidden="hidden"
  >
    <template #header>
      <header class="task-modal__header">
        <div class="task-modal__location">
          <i class="iconoir-check-circle" aria-hidden="true"></i>
          <span :title="database.name">{{ database.name }}</span>
          <i class="iconoir-nav-arrow-right" aria-hidden="true"></i>
          <strong :title="table.name">{{ table.name }}</strong>
        </div>
        <div class="task-modal__tools">
          <template v-if="enableNavigation">
            <button
              type="button"
              class="task-modal__icon-button"
              :aria-label="$t('taskPanel.previous')"
              :title="$t('taskPanel.previous')"
              :disabled="navigationLoading"
              @click="$emit('navigate-previous', previousRow)"
            >
              <i class="iconoir-nav-arrow-up" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="task-modal__icon-button"
              :aria-label="$t('taskPanel.next')"
              :title="$t('taskPanel.next')"
              :disabled="navigationLoading"
              @click="$emit('navigate-next', nextRow)"
            >
              <i class="iconoir-nav-arrow-down" aria-hidden="true"></i>
            </button>
          </template>
          <button
            v-if="
              !readOnly &&
              row.id &&
              $hasPermission(
                'database.table.delete_row',
                table,
                database.workspace.id
              )
            "
            type="button"
            class="task-modal__icon-button"
            :title="$t('transferRow.title')"
            :aria-label="$t('transferRow.title')"
            @click="$refs.transferModal.show()"
          >
            <i class="iconoir-arrow-right" aria-hidden="true"></i>
          </button>
          <TransferRowModal
            ref="transferModal"
            :database="database"
            :table="table"
            :row="row"
            @transferred="$refs.modal.hide()"
          />
          <component
            :is="actionComponent"
            v-for="(actionComponent, i) in rowModalActionComponents"
            :key="i"
            :database="database"
            :table="table"
            :row="row"
            :view="view"
          />
          <button
            type="button"
            class="task-modal__icon-button"
            :aria-label="$t('taskPanel.close')"
            :title="$t('taskPanel.close')"
            @click="$refs.modal.hide()"
          >
            <i class="iconoir-cancel" aria-hidden="true"></i>
          </button>
        </div>
        <nav
          v-if="hasRightSidebar"
          class="task-modal__mobile-nav"
          :aria-label="$t('taskPanel.navigation')"
        >
          <button type="button" @click="scrollToPanel('details')">
            {{ $t('taskPanel.details') }}
          </button>
          <button type="button" @click="scrollToPanel('activity')">
            <i class="iconoir-chat-bubble" aria-hidden="true"></i
            >{{ $t('taskPanel.activity') }}
          </button>
        </nav>
      </header>
    </template>
    <template #content>
      <div
        class="task-modal__details"
        :class="{ 'task-modal__details--standalone': !hasRightSidebar }"
      >
        <div ref="details" class="task-modal__intro" tabindex="-1">
          <span class="row-modal__item-chip"
            ><i class="iconoir-circle" aria-hidden="true"></i
            >{{ $t('rowEditModal.itemChip') }}</span
          >
          <span v-if="row.id" class="task-modal__reference">#{{ row.id }}</span>
          <span v-if="readOnly" class="task-modal__reference"
            ><i class="iconoir-lock" aria-hidden="true"></i
            >{{ $t('taskPanel.readOnly') }}</span
          >
        </div>
        <RowEditModalFieldsList
          :primary-is-sortable="primaryIsSortable"
          :fields="visibleFields"
          :sortable="!readOnly && fieldsSortable"
          :can-modify-fields="!readOnly && canModifyFields"
          :hidden="false"
          :read-only="readOnly"
          :row="row"
          :view="view"
          :table="table"
          :database="database"
          :all-fields-in-table="allFieldsInTable"
          @field-updated="$emit('field-updated', $event)"
          @field-deleted="$emit('field-deleted')"
          @order-fields="$emit('order-fields', $event)"
          @toggle-field-visibility="$emit('toggle-field-visibility', $event)"
          @update="update"
          @refresh-row="$emit('refresh-row', $event)"
        ></RowEditModalFieldsList>
        <RowEditModalFieldsList
          v-if="hiddenFields.length"
          :primary-is-sortable="primaryIsSortable"
          :fields="hiddenFields"
          :can-modify-fields="!readOnly && canModifyFields"
          :sortable="false"
          :hidden="true"
          :read-only="readOnly"
          :row="row"
          :view="view"
          :table="table"
          :database="database"
          :all-fields-in-table="allFieldsInTable"
          @field-updated="$emit('field-updated', $event)"
          @field-deleted="$emit('field-deleted')"
          @toggle-field-visibility="$emit('toggle-field-visibility', $event)"
          @update="update"
          @refresh-row="$emit('refresh-row', $event)"
        >
        </RowEditModalFieldsList>
        <div
          v-if="
            !readOnly &&
            canModifyFields &&
            $hasPermission(
              'database.table.create_field',
              table,
              database.workspace.id
            )
          "
          class="actions task-modal__footer"
        >
          <!-- Origem Flow: ação "Adicionar subtarefa" do quadro do ClickUp —
             só nas tabelas com o par de campos de subtarefas; Enter cria
             em série, como no grid. -->
          <div v-if="subtasksAvailable" class="row-modal__subtask-action">
            <ButtonText
              v-if="!addingSubtask"
              tag="a"
              icon="iconoir-git-fork"
              @click="startAddingSubtask"
            >
              {{ $t('rowEditModal.addSubtask') }}
            </ButtonText>
            <template v-else>
              <span
                class="grid-view__row-status grid-view__row-status--empty row-modal__subtask-status"
              ></span>
              <input
                ref="newSubtaskInput"
                v-model="newSubtaskName"
                class="row-modal__subtask-input"
                :placeholder="$t('gridView.subtaskNamePlaceholder')"
                :disabled="creatingSubtask"
                @keydown.enter="submitNewSubtask"
                @keydown.esc="cancelNewSubtask"
              />
              <a class="row-modal__subtask-cancel" @click="cancelNewSubtask">{{
                $t('action.cancel')
              }}</a>
              <a class="row-modal__subtask-save" @click="submitNewSubtask"
                >{{ $t('gridView.saveSubtask') }}
                <span class="row-modal__subtask-save-key">↵</span></a
              >
            </template>
          </div>
          <span ref="createFieldContextLink">
            <ButtonText
              tag="a"
              icon="iconoir-plus"
              @click="
                $refs.createFieldContext.toggle($refs.createFieldContextLink)
              "
            >
              {{ $t('rowEditModal.addField') }}
            </ButtonText></span
          >

          <CreateFieldContext
            ref="createFieldContext"
            :table="table"
            :view="view"
            :all-fields-in-table="allFieldsInTable"
            :database="database"
            @field-created="$emit('field-created', $event)"
            @field-created-callback-done="
              $emit('field-created-callback-done', $event)
            "
          ></CreateFieldContext>
        </div>
        <RowLinkedDocuments
          v-if="row && row.id"
          :database="database"
          :table="table"
          :row="row"
        ></RowLinkedDocuments>
      </div>
    </template>
    <template #sidebar>
      <h2 ref="activity" class="task-modal__activity-title" tabindex="-1">
        <i class="iconoir-chat-bubble" aria-hidden="true"></i
        >{{ $t('taskPanel.activity') }}
      </h2>
      <RowEditModalSidebar
        :row="row"
        :table="table"
        :database="database"
        :fields="allFieldsInTable"
        :view="view"
        :read-only="readOnly"
      ></RowEditModalSidebar>
    </template>
  </Modal>
</template>

<script>
import { mapGetters } from 'vuex'
import modal from '@baserow/modules/core/mixins/modal'
import rowModalFieldWalk from '@baserow/modules/database/mixins/rowModalFieldWalk'
import CreateFieldContext from '@baserow/modules/database/components/field/CreateFieldContext'
import RowEditModalFieldsList from './RowEditModalFieldsList.vue'
import RowEditModalSidebar from './RowEditModalSidebar.vue'
import RowLinkedDocuments from './RowLinkedDocuments.vue'
import TransferRowModal from './TransferRowModal.vue'
import { getPrimaryOrFirstField } from '@baserow/modules/database/utils/field'
import {
  getParentTaskField,
  getSubtasksField,
} from '@baserow/modules/database/utils/subtasks'
import { getStatusField } from '@baserow/modules/database/utils/dueDate'
import RowService from '@baserow/modules/database/services/row'
import { notifyIf } from '@baserow/modules/core/utils/error'

export default {
  name: 'RowEditModal',
  components: {
    CreateFieldContext,
    RowEditModalFieldsList,
    RowEditModalSidebar,
    RowLinkedDocuments,
    TransferRowModal,
  },
  mixins: [modal, rowModalFieldWalk],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: [Object, null],
      required: false,
      default: null,
    },
    allFieldsInTable: {
      type: Array,
      required: true,
    },
    primaryIsSortable: {
      type: Boolean,
      required: false,
      default: false,
    },
    visibleFields: {
      type: Array,
      required: true,
    },
    hiddenFields: {
      type: Array,
      required: false,
      default: () => [],
    },
    showHiddenFields: {
      type: Boolean,
      required: false,
      default: false,
    },
    rows: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    enableNavigation: {
      type: Boolean,
      required: false,
      default: false,
    },
    fieldsSortable: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    canModifyFields: {
      type: Boolean,
      required: false,
      default: () => true,
    },
  },
  emits: [
    'field-updated',
    'field-deleted',
    'order-fields',
    'hidden',
    'update',
    'navigate-previous',
    'navigate-next',
    'toggle-field-visibility',
    'toggle-hidden-fields-visibility',
    'refresh-row',
    'field-created',
    'field-created-callback-done',
  ],
  data() {
    return {
      // Origem Flow: ação "Adicionar subtarefa" (quadro do ClickUp).
      addingSubtask: false,
      creatingSubtask: false,
      newSubtaskName: '',
    }
  },
  computed: {
    ...mapGetters({
      navigationLoading: 'rowModalNavigation/getLoading',
    }),
    /**
     * Origem Flow: a ação de subtarefa só aparece quando a tabela tem o
     * par de campos do modelo (mãe "Subtarefas" / filha "Tarefa
     * principal") e a linha já existe.
     */
    subtasksAvailable() {
      return (
        !this.readOnly &&
        !!this.row.id &&
        getParentTaskField(this.allFieldsInTable, this.table.id) !== null &&
        getSubtasksField(this.allFieldsInTable, this.table.id) !== null
      )
    },
    modalRow() {
      return this.$store.getters['rowModal/get'](this.$.uid)
    },
    rowId() {
      return this.modalRow.id
    },
    rowExists() {
      return this.modalRow.exists
    },
    row() {
      return this.modalRow.row
    },
    rowIndex() {
      return this.rows.findIndex((r) => r !== null && r.id === this.rowId)
    },
    nextRow() {
      return this.rowIndex !== -1 && this.rows.length > this.rowIndex + 1
        ? this.rows[this.rowIndex + 1]
        : null
    },
    previousRow() {
      return this.rowIndex > 0 ? this.rows[this.rowIndex - 1] : null
    },
    heading() {
      const field = getPrimaryOrFirstField(this.visibleFields)

      if (!field) {
        return null
      }

      const name = `field_${field.id}`
      if (Object.prototype.hasOwnProperty.call(this.row, name)) {
        return this.$registry
          .get('field', field.type)
          .toHumanReadableString(field, this.row[name])
      } else {
        return null
      }
    },
    activeSidebarTypes() {
      const allSidebarTypes = this.$registry.getOrderedList('rowModalSidebar')
      return allSidebarTypes.filter(
        (type) =>
          type.isDeactivated(
            this.database,
            this.table,
            this.readOnly,
            this.view
          ) === false && type.getComponent()
      )
    },
    hasRightSidebar() {
      return this.activeSidebarTypes.length > 0
    },
    canSubscribeToRowUpdates() {
      return (
        this.$hasPermission(
          'database.table.listen_to_all',
          this.table,
          this.database.workspace.id
        ) &&
        // If the row ID is not an integer, it could mean that the row hasn't been
        // created in the backend yet.
        Number.isInteger(this.rowId)
      )
    },
    rowModalActionComponents() {
      return this.activeSidebarTypes
        .map((type) => type.getActionComponent(this.row))
        .filter((actionComponent) => actionComponent !== null)
    },
  },
  watch: {
    /**
     * It could happen that the view doesn't always have all the rows buffered. When
     * the modal is opened, it will find the correct row by looking through all the
     * rows of the view. If a filter changes, the existing row could be removed from the
     * buffer while the user still wants to edit the row because the modal is open. In
     * that case, we will keep a copy in the `rowModal` store, which will also listen
     * for real time update events to make sure the latest information is always
     * visible. If the buffer of the view changes and the row does exist, we want to
     * switch to that version to maintain reactivity between the two.
     */
    rows(value) {
      const row = value.find((r) => r !== null && r.id === this.rowId)
      if (row === undefined && this.rowExists) {
        this.$store.dispatch('rowModal/doesNotExist', {
          componentId: this.$.uid,
        })
      } else if (row !== undefined && !this.rowExists) {
        this.$store.dispatch('rowModal/doesExist', {
          componentId: this.$.uid,
          row,
        })
      } else if (row !== undefined) {
        // If the row already exists and it has changed, we need to replace it,
        // otherwise we might loose reactivity.
        this.$store.dispatch('rowModal/replace', {
          componentId: this.$.uid,
          row,
        })
      }
    },
    rowId(newValue, oldValue) {
      if (this.canSubscribeToRowUpdates) {
        if (oldValue > 0) {
          this.$realtime.unsubscribe('row', {
            table_id: this.table.id,
            row_id: oldValue,
          })
        }
        if (newValue > 0) {
          this.$realtime.subscribe('row', {
            table_id: this.table.id,
            row_id: newValue,
          })
        }
      }
    },
  },
  methods: {
    scrollToPanel(panel) {
      const element = this.$refs[panel]
      element?.scrollIntoView({ block: 'start' })
      element?.focus({ preventScroll: true })
    },
    /**
     * Origem Flow: mesma usabilidade do grid — input inline, Enter cria em
     * série, Esc cancela. A filha nasce com "Tarefa principal" apontando
     * para esta linha (via API direta; o filtro de raiz esconde a filha) e
     * o vínculo "Subtarefas" entra na mãe por update direto — o websocket
     * propaga para todas as views abertas.
     */
    startAddingSubtask() {
      this.addingSubtask = true
      this.$nextTick(() => {
        const input = this.$refs.newSubtaskInput
        if (input) {
          input.focus()
        }
      })
    },
    cancelNewSubtask() {
      this.addingSubtask = false
      this.newSubtaskName = ''
    },
    async submitNewSubtask() {
      const name = this.newSubtaskName.trim()
      if (name === '' || this.creatingSubtask) {
        return
      }
      this.creatingSubtask = true
      try {
        const parentField = getParentTaskField(
          this.allFieldsInTable,
          this.table.id
        )
        const subtasksField = getSubtasksField(
          this.allFieldsInTable,
          this.table.id
        )
        const primaryField = getPrimaryOrFirstField(this.allFieldsInTable)
        const values = {
          [`field_${primaryField.id}`]: name,
          [`field_${parentField.id}`]: [this.row.id],
        }
        const statusField = getStatusField(this.allFieldsInTable)
        const inheritedStatus =
          statusField && this.row[`field_${statusField.id}`]
        if (inheritedStatus) {
          values[`field_${statusField.id}`] = inheritedStatus.id
        }
        const { data: newRow } = await RowService(this.$client).create(
          this.table.id,
          values
        )
        const current = (this.row[`field_${subtasksField.id}`] || []).map(
          (link) => link.id
        )
        await RowService(this.$client).update(this.table.id, this.row.id, {
          [`field_${subtasksField.id}`]: [...current, newRow.id],
        })
        this.$store.dispatch('toast/info', {
          title: this.$t('rowEditModal.subtaskCreatedTitle'),
          message: this.$t('rowEditModal.subtaskCreated', { name }),
        })
        this.newSubtaskName = ''
        this.$nextTick(() => {
          const input = this.$refs.newSubtaskInput
          if (input) {
            input.focus()
          }
        })
      } catch (error) {
        notifyIf(error, 'row')
      } finally {
        this.creatingSubtask = false
      }
    },
    show(rowId, rowFallback = {}, ...args) {
      const row = this.rows.find((r) => r !== null && r.id === rowId)
      this.$store.dispatch('rowModal/open', {
        tableId: this.table.id,
        componentId: this.$.uid,
        id: rowId,
        row: row || rowFallback,
        exists: !!row,
      })
      if (this.canSubscribeToRowUpdates) {
        this.$realtime.subscribe('row', {
          table_id: this.table.id,
          row_id: rowId,
        })
      }
      this.getRootModal().show(...args)
    },
    hidden(...args) {
      if (this.canSubscribeToRowUpdates) {
        this.$realtime.unsubscribe('row', {
          table_id: this.table.id,
          row_id: this.rowId,
        })
      }
      this.$store.dispatch('rowModal/clear', { componentId: this.$.uid })
      this.$emit('hidden', { row: this.row })
    },
    /**
     * Because the modal can't update values by himself, an event will be called to
     * notify the parent component to actually update the value.
     */
    update(context) {
      context.table = this.table
      this.$emit('update', context)
    },
  },
}
</script>
